/**
 * Save migration for the original sect roster (docs/design/SECT_ROSTER_ORIGINAL.md, 2026-10-13): saves made
 * before the rename hold the old school / art / neigong ids. `migrateSectIds` maps them to the new ids
 * anywhere in a parsed save (values and object keys), and is a no-op on saves that already use the new ids.
 * Pure data, no clock or randomness. The same map drives scripts/migrations/sect-roster-2026-10-13.py.
 */

const SCHOOL_IDS: Readonly<Record<string, string>> = {
  wudang: 'mist-peak',
  shaolin: 'bell-stone',
  jinyiwei: 'lantern-road',
  jilegu: 'dim-moon',
  junzitang: 'pine-ink',
  bloodsabre: 'cinnabar',
};

const ART_IDS: Readonly<Record<string, string>> = {
  'taiji-true': 'mist-wheel',
  'taiji-fist': 'drift-fist',
  'taiji-sword': 'drift-sword',
  'arhat-fist': 'bell-fist',
  'prajna-palm': 'hum-palm',
  'demon-sword': 'founder-sabre',
  'brocade-sabre': 'lamp-hook-sabre',
  'arrest-hand': 'chain-hold',
  'spring-sabre': 'quick-draw-sabre',
  'iron-guard': 'road-iron',
  'flying-fish': 'lamp-spark',
  'bliss-palm': 'quiet-palm',
  'moon-sword': 'listening-blade',
  'demon-dance': 'chorus',
  'bliss-heart': 'mourning-heart',
  'heaven-demon': 'vigil-heart',
  'brush-sword': 'ink-slate',
  'six-pulse': 'pulse-seal',
  'sage-sword': 'one-cut',
  'sage-heart': 'ink-stillness',
  'blood-sabre': 'heat-sabre',
  'blood-claw': 'tongs-claw',
  'red-cliff-sabre': 'cliff-push-sabre',
  'blood-heart': 'ember-heart',
  'blood-sea': 'ember-sea',
  yijin: 'bell-body',
  marrow: 'bell-layers',
  gentleman: 'ink-breath',
};

const STATUS_IDS: Readonly<Record<string, string>> = {
  'np-taiji2': 'np-mist-wheel',
  'np-taiji': 'np-mist-breath',
  'np-marrow': 'np-bell-layers',
};

/** `taiji` is also an element / fx name, so it is only the neigong when it sits under one of these keys. */
const TAIJI_KEYS = new Set(['neigong', 'activeNeigong']);

/** The new id for an old one (exact id, or `disciple-` / `sect-` / `recruiter-` + a school id); the input otherwise. */
export function migrateSectId(id: string): string {
  const direct = SCHOOL_IDS[id] ?? ART_IDS[id] ?? STATUS_IDS[id];
  if (direct) return direct;
  const m = /^(disciple|sect|recruiter)-(.+)$/.exec(id);
  if (m && SCHOOL_IDS[m[2]!]) return `${m[1]}-${SCHOOL_IDS[m[2]!]}`;
  return id;
}

function walk(v: unknown, key: string | null): unknown {
  if (typeof v === 'string') return v === 'taiji' && key && TAIJI_KEYS.has(key) ? 'mist-breath' : migrateSectId(v);
  if (Array.isArray(v)) return v.map((x) => walk(x, key));
  if (v && typeof v === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, x] of Object.entries(v)) {
      const nk = k === 'taiji' && key !== null && TAIJI_KEYS.has(key) ? 'mist-breath' : migrateSectId(k);
      out[nk] = walk(x, nk === 'neigong' || nk === 'activeNeigong' ? nk : k);
    }
    return out;
  }
  return v;
}

/** A parsed save with every old sect, art and neigong id replaced by its new one. */
export function migrateSectIds<T>(raw: T): T {
  return walk(raw, null) as T;
}
